import { Component } from '@angular/core';

@Component({
    selector: 'sunset-decision',
    template: 
    `
    <pet-page-template [data]='pageData' [additionalContent]='additionalContent' [status]='"Draft"'></pet-page-template>
    `,
    styles: ``
  })
  
export class SunsetDecisionComponent {
    public pageData: any = [
      {
        type: 'intro',
        value: {
          mainText: 'Introduction',
          subText:
            'Sunset Decision is the moment where we intentionally decide that a product, module, or major capability should no longer continue on the same trajectory. The goal is not to write a new business case, but to provide a clear, concise justification of why continuing is no longer the right thing to do. This includes checking strategic fit, financial viability, and technical feasibility, and identifying a reasonable replacement or alternate path for customers before moving into transition and closure.'
        }
      },
  
      {
        type: 'tileItem',
        value: {
          title: 'Roles and Responsibilities',
          items: [
            {
              name: 'Product VP',
              description:
                'Owns the overall portfolio direction and approves sunset decisions for products or major capabilities to ensure they are consistent with strategy and commercial priorities.'
            },
            {
              name: 'Product Manager',
              description:
                'Leads the sunset assessment, consolidates inputs from strategy, finance, technology, and customer-facing teams, and proposes the EOL justification and replacement path.'
            },
            {
              name: 'Tech Lead / Architect',
              description:
                'Assesses technical feasibility of continued investment versus retirement, including risks, dependencies, and effort required to keep the product viable.'
            },
            {
              name: 'Customer Success',
              description:
                'Provides customer impact insights, usage patterns, key accounts at risk, and early signals from the field that support or challenge the sunset proposal.'
            },
            {
              name: 'Business Controller',
              description:
                'Supports financial analysis of revenue, cost-to-serve, margins, and long-term economics, helping quantify the business case for sunsetting.'
            },
            {
              name: 'Support / Delivery',
              description:
                'Highlights operational complexity, support load, and known pain points that indicate whether the product remains sustainable from a service perspective.'
            }
          ]
        }
      },
  
      {
        type: 'checkListItem',
        value: {
          title: 'Prerequisites',
          items: [
            {
              id: 1,
              text:
                'Basic clarity on the current product scope (what is in and out of the proposed sunset decision).'
            },
            {
              id: 2,
              text:
                'Recent view of key product metrics such as revenue, usage, cost-to-serve, technical health, and support effort.'
            },
            {
              id: 3,
              text:
                'High-level understanding of available alternatives for customers (other products, modules, partners, or manual processes).'
            },
            {
              id: 4,
              text:
                'Agreement between Product and Tech Lead that a sunset assessment is warranted and should be formally initiated.'
            }
          ]
        }
      },
  
      {
        type: "superListItem",
        value: {
          title: "Process Steps",
          items: [
            {
              title: "Check Strategic Alignment",
              description: {
                "Objective": [
                  "Assess whether the product still contributes to the portfolio strategy, target customer segments, and long-term business direction."
                ],
                "Activity": [
                  "Review strategic priorities, portfolio overlaps, and shifts in customer or market expectations.",
                  "Identify misalignments where the product no longer supports regulatory, commercial, or competitive needs."
                ],
                "Outcome": [
                  "A clear statement summarizing whether the product remains strategically relevant."
                ]
              }
            },
            {
              title: "Assess Financial Viability",
              description: {
                "Objective": [
                  "Determine if ongoing investment in the product is financially justified compared to other opportunities in the portfolio."
                ],
                "Activity": [
                  "Examine revenue trends, cost-to-serve, margins, and the investment required to maintain or modernize the product.",
                  "Compare the product’s financial performance and future projections against more strategic initiatives."
                ],
                "Outcome": [
                  "Concise business justification describing why continuing investment is or isn’t viable."
                ]
              }
            },
            {
              title: "Assess Technical Feasibility and Risk",
              description: {
                "Objective": [
                  "Understand whether the product’s technical foundation remains sustainable, secure, and maintainable."
                ],
                "Activity": [
                  "Review technical debt, technology obsolescence, performance issues, and dependencies on unsupported components.",
                  "Evaluate the long-term effort and risk required to keep the product technically viable."
                ],
                "Outcome": [
                  "Documented technical justification summarizing key risks and feasibility concerns."
                ]
              }
            },
            {
              title: "Identify Replacement or Alternate Path",
              description: {
                "Objective": [
                  "Ensure customers will have a viable way to achieve their outcomes after the product is sunset."
                ],
                "Activity": [
                  "Determine whether an internal product, new module, partner solution, or defined workaround can serve as the replacement.",
                  "Validate feasibility with Product, Customer Success, and solution teams."
                ],
                "Outcome": [
                  "A high-level replacement or transition path that guides later customer communication and planning."
                ]
              }
            },
            {
              title: "Decide and Record the Sunset",
              description: {
                "Objective": [
                  "Formally conclude the assessment and capture a clear, lightweight decision that the product should be sunset."
                ],
                "Activity": [
                  "Consolidate strategic, financial, and technical inputs into a short decision note (not a business case).",
                  "Present the recommendation to the Product VP for approval, ensuring Finance and Customer Success have provided input where relevant."
                ],
                "Outcome": [
                  "A formally approved Sunset Decision, led by the Product Manager and authorised by the Product VP, which becomes the basis for Customer Transition & Communication and Technical & Operational Closure."
                ]
              }
            }
          ]
        }
      },
  
      {
        type: 'multibulletList',
        value: {
          title: 'Inputs and Evidence That Support the Decision',
          items: [
            {
              name: 'Product and Business Performance',
              description: [
                'Description: Revenue trends, margins, churn, NRR/ARR contribution, and pipeline health for the product.',
                'Example: Revenue flat or declining over multiple periods while cost-to-serve and required investment are increasing.'
              ]
            },
            {
              name: 'Customer and Market Signals',
              description: [
                'Description: Usage levels, feature adoption, customer feedback, NPS/CSAT scores, and competitive positioning.',
                'Example: Low active usage, repeated feedback that customers are moving to a different solution, or stronger strategic focus on another product in the same space.'
              ]
            },
            {
              name: 'Technical Health and Risk',
              description: [
                'Description: Security posture, vulnerability profile, technology obsolescence, performance and stability issues, and dependency on legacy components.',
                'Example: Core components are no longer supported by vendors or require significant effort to keep patched and secure.'
              ]
            },
            {
              name: 'Operational Load and Complexity',
              description: [
                'Description: Support ticket volume, incident patterns, custom one-off implementations, and operational workarounds.',
                'Example: High frequency of complex support cases for a small set of customers that does not justify the continued operational burden.'
              ]
            },
            {
              name: 'Risk and Compliance Considerations',
              description: [
                'Description: Regulatory obligations, contractual commitments, and risks of continuing or stopping the service.',
                'Example: Continuing the product would require substantial investment to remain compliant, with limited commercial upside.'
              ]
            }
          ]
        }
      },
  
      {
        type: 'listItem',
        value: {
          title: 'Outcomes of a Good Sunset Decision',
          items: [
            {
              name: 'Clarity of direction',
              description:
                'It is clear to the organisation why the product should not continue in its current form and how this supports portfolio and strategy decisions.'
            },
            {
              name: 'Concise EOL justification',
              description:
                'Business and technical EOL justification is captured in a short, understandable format that can be referred to later without needing a full business case.'
            },
            {
              name: 'Defined replacement path',
              description:
                'There is a documented high-level replacement or alternate path for customers that can be further detailed during the transition planning phase.'
            },
            {
              name: 'Alignment across key stakeholders',
              description:
                'Product, Technology, Finance, and Customer-facing teams share a common understanding of the decision and are prepared to move into transition and closure activities.'
            }
          ]
        }
      }
    ];
  
    public additionalContent = [
      {
        type: 'tips',
        title: 'Tips',
        value:
          'A Sunset Decision is a sign of product and portfolio maturity, not failure. Keep the decision lightweight but evidence-based: focus on a short justification, a clear replacement path, and the minimum set of stakeholders needed to approve and support the outcome.'
      },
      {
        type: 'attachments',
        title: 'Templates / Guides',
        value: [
          {
            name: 'Sunset Decision One-Pager Template (underway)',
            url: '_self'
          },
          {
            name: 'EOL Business & Technical Checklist (underway)',
            url: '_self'
          }
        ]
      },
      {
        type: 'optionalContent',
        title: 'Benefits and Repercussions',
        value: [
          {
            key: 'Benefits',
            data: [
              'Frees up capacity and investment for products and capabilities that are more aligned with strategy and growth.',
              'Reduces long-term technical, operational, and compliance risks from underinvested or legacy products.',
              'Creates a clear narrative for customers and internal teams instead of letting products slowly become “zombies” in the portfolio.'
            ]
          },
          {
            key: 'Repercussions',
            data: [
              'Delaying or avoiding sunset decisions can lead to fragmented portfolios, confused customers, and duplicated investments.',
              'Maintaining non-viable products increases hidden costs and risk without corresponding value.',
              'Unclear decisions can result in sudden, unplanned shutdowns that undermine customer trust and make transitions harder later.'
            ]
          }
        ]
      }
    ];
  }